/// <reference lib="dom"/>
import type { WbaxAutomation, WbaxRamp } from "@/types.tsx";

export function ApplyAutomation(param: AudioParam, ramps: WbaxAutomation, base: number) {
  for (const ramp of ramps) ApplyRamp(param, ramp, base);
}

export function ApplyRamp(param: AudioParam, ramp: WbaxRamp, base: number) {
  if ("set" in ramp) {
    param.setValueAtTime(ramp.set, base + (ramp.at ?? 0));
  } else if ("linear" in ramp) {
    param.linearRampToValueAtTime(ramp.linear, base + ramp.at);
  } else if ("exp" in ramp) {
    param.exponentialRampToValueAtTime(ramp.exp, base + ramp.at);
  } else if ("target" in ramp) {
    param.setTargetAtTime(ramp.target, base + ramp.at, ramp.tau);
  } else if ("curve" in ramp) {
    param.setValueCurveAtTime(Float32Array.from(ramp.curve), base + ramp.at, ramp.duration);
  }
}

export function AutomationEnd(ramps: WbaxAutomation): number {
  return ramps.reduce(
    (end, r) => Math.max(end, ("at" in r ? r.at ?? 0 : 0) + ("duration" in r ? r.duration : 0)),
    0,
  );
}

export type WbaxCurveKind = "linear" | "exp";

const step = (kind: WbaxCurveKind, value: number, at: number): WbaxRamp =>
  kind === "exp" ? { exp: Math.max(value, 0.0001), at } : { linear: value, at };

export interface AdsrProps {
  from?: number;
  peak?: number;
  attack?: number;
  hold?: number;
  decay?: number;
  sustain?: number;
  sustainFor?: number;
  release?: number;
  end?: number;
  curve?: WbaxCurveKind;
}

export function adsr(props: AdsrProps = {}): WbaxAutomation {
  const {
    from = 0.0001,
    peak = 1,
    attack = 0.01,
    hold = 0,
    decay = 0.1,
    sustain = 0.5,
    sustainFor = 0,
    release = 0.2,
    end = 0,
    curve = "linear",
  } = props;
  const a = attack;
  const h = a + hold;
  const d = h + decay;
  const s = d + sustainFor;
  const r = s + release;
  return [
    { set: from, at: 0 },
    step(curve, peak, a),
    ...(hold > 0 ? [{ set: peak, at: h }] : []),
    step(curve, sustain, d),
    ...(sustainFor > 0 ? [{ set: sustain, at: s }] : []),
    step(curve, end, r),
  ];
}

export interface PercProps {
  peak?: number;
  attack?: number;
  decay?: number;
  end?: number;
  curve?: WbaxCurveKind;
}

export function perc(props: PercProps = {}): WbaxAutomation {
  const { peak = 1, attack = 0.001, decay = 0.3, end = 0.0001, curve = "exp" } = props;
  return [
    { set: attack > 0 ? 0.0001 : peak, at: 0 },
    ...(attack > 0 ? [step("linear", peak, attack)] : []),
    step(curve, end, attack + decay),
  ];
}

export function sweep(
  from: number,
  to: number,
  duration: number,
  curve: WbaxCurveKind = "exp",
  at = 0,
): WbaxAutomation {
  return [{ set: from, at }, step(curve, to, at + duration)];
}
